import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useToast } from 'src/context/ToastContext';
import { RebateRuleWithId, SupplierPayment } from 'src/api/rebates/types';
import config from 'src/config';
import { ProductGroup } from '@/api/rebates/useProductGroups';
import useDeleteRebateAgreement from '@/api/rebates/useDeleteRebateAgreement';

interface UseAgreementDetailStateProps {
  agreementId: string;
  refetchAgreement: () => void;
  refetchProductGroups: () => void;
  refetchRebateRules: () => void;
  refetchPayments: () => void;
}

export const useAgreementDetailState = ({
  agreementId,
  refetchAgreement,
  refetchProductGroups,
  refetchRebateRules,
  refetchPayments,
}: UseAgreementDetailStateProps) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const deleteAgreementMutation = useDeleteRebateAgreement(agreementId);

  // UI State
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['status', 'rebateRules']),
  );
  const [expandedRules, setExpandedRules] = useState<Set<string>>(new Set());

  // Product Group Modal States
  const [productGroupModal, setProductGroupModal] = useState({
    open: false,
    editing: null,
    deleting: false,
    deleteConfirmOpen: false,
    itemToDelete: null as ProductGroup | null,
  });

  // Rebate Rule Modal States
  const [rebateRuleModal, setRebateRuleModal] = useState({
    open: false,
    editing: null,
    deleting: false,
    deleteConfirmOpen: false,
    itemToDelete: null as RebateRuleWithId | null,
  });

  // Payment Modal States
  const [paymentModal, setPaymentModal] = useState({
    open: false,
    deleting: false,
    deleteConfirmOpen: false,
    itemToDelete: null as SupplierPayment | null,
  });

  // Agreement Delete State
  const [agreementDeleteModal, setAgreementDeleteModal] = useState({
    deleteConfirmOpen: false,
    deleting: false,
  });

  // Accordion Handlers
  const handleAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      const newExpandedSections = new Set(expandedSections);
      if (isExpanded) {
        newExpandedSections.add(panel);
      } else {
        newExpandedSections.delete(panel);
      }
      setExpandedSections(newExpandedSections);
    };

  const toggleRuleExpansion = (ruleId: string) => {
    setExpandedRules((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(ruleId)) {
        newSet.delete(ruleId);
      } else {
        newSet.add(ruleId);
      }
      return newSet;
    });
  };

  // Product Group Handlers
  const productGroupHandlers = {
    create: () => {
      setProductGroupModal((prev) => ({ ...prev, open: true, editing: null }));
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    edit: (productGroup: any) => {
      setProductGroupModal((prev) => ({
        ...prev,
        open: true,
        editing: productGroup,
      }));
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    delete: (productGroup: any) => {
      setProductGroupModal((prev) => ({
        ...prev,
        deleteConfirmOpen: true,
        itemToDelete: productGroup,
      }));
    },
    confirmDelete: async () => {
      if (!productGroupModal.itemToDelete) return;

      setProductGroupModal((prev) => ({ ...prev, deleting: true }));
      try {
        const response = await fetch(
          `${config.apiBaseUrl}/rebates/product-groups/${productGroupModal.itemToDelete.id}`,
          { method: 'DELETE', credentials: 'include' },
        );

        if (!response.ok) {
          throw new Error('Failed to delete product group');
        }

        refetchProductGroups();
        refetchAgreement();
        setProductGroupModal((prev) => ({
          ...prev,
          deleteConfirmOpen: false,
          itemToDelete: null,
        }));
        showToast({
          message: 'Product group deleted successfully',
          color: 'success',
        });
      } catch (error) {
        console.error('Error deleting product group:', error);
        showToast({
          message: 'Failed to delete product group',
          color: 'error',
        });
      } finally {
        setProductGroupModal((prev) => ({
          ...prev,
          deleting: false,
          deleteConfirmOpen: false,
          itemToDelete: null,
        }));
      }
    },
    cancelDelete: () => {
      setProductGroupModal((prev) => ({
        ...prev,
        deleteConfirmOpen: false,
        itemToDelete: null,
      }));
    },
    closeModal: () => {
      setProductGroupModal((prev) => ({ ...prev, open: false, editing: null }));
    },
    onSuccess: () => {
      refetchProductGroups();
      refetchAgreement();
      setProductGroupModal((prev) => ({ ...prev, open: false, editing: null }));
    },
  };

  // Rebate Rule Handlers
  const rebateRuleHandlers = {
    create: () => {
      setRebateRuleModal((prev) => ({ ...prev, open: true, editing: null }));
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    edit: (rule: any) => {
      setRebateRuleModal((prev) => ({ ...prev, open: true, editing: rule }));
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    delete: (rule: any) => {
      setRebateRuleModal((prev) => ({
        ...prev,
        deleteConfirmOpen: true,
        itemToDelete: rule,
      }));
    },
    confirmDelete: async () => {
      if (!rebateRuleModal.itemToDelete) return;

      setRebateRuleModal((prev) => ({ ...prev, deleting: true }));
      try {
        const response = await fetch(
          `${config.apiBaseUrl}/rebates/rules/${rebateRuleModal.itemToDelete.id}`,
          { method: 'DELETE', credentials: 'include' },
        );

        if (!response.ok) {
          throw new Error('Failed to delete rebate rule');
        }

        refetchRebateRules();
        refetchAgreement();
        setRebateRuleModal((prev) => ({
          ...prev,
          deleteConfirmOpen: false,
          itemToDelete: null,
        }));
        showToast({
          message: 'Rebate rule deleted successfully',
          color: 'success',
        });
      } catch (error) {
        console.error('Error deleting rebate rule:', error);
        showToast({ message: 'Failed to delete rebate rule', color: 'error' });
      } finally {
        setRebateRuleModal((prev) => ({
          ...prev,
          deleting: false,
          deleteConfirmOpen: false,
          itemToDelete: null,
        }));
      }
    },
    cancelDelete: () => {
      setRebateRuleModal((prev) => ({
        ...prev,
        deleteConfirmOpen: false,
        itemToDelete: null,
      }));
    },
    closeModal: () => {
      setRebateRuleModal((prev) => ({ ...prev, open: false, editing: null }));
    },
    onSuccess: () => {
      refetchRebateRules();
      refetchAgreement();
      setRebateRuleModal((prev) => ({ ...prev, open: false, editing: null }));
    },
  };

  // Payment Handlers
  const paymentHandlers = {
    create: () => {
      setPaymentModal((prev) => ({ ...prev, open: true }));
    },
    delete: (payment: SupplierPayment) => {
      setPaymentModal((prev) => ({
        ...prev,
        deleteConfirmOpen: true,
        itemToDelete: payment,
      }));
    },
    confirmDelete: async () => {
      if (!paymentModal.itemToDelete) return;

      setPaymentModal((prev) => ({ ...prev, deleting: true }));
      try {
        const response = await fetch(
          `${config.apiBaseUrl}/rebates/supplier-payments/${paymentModal.itemToDelete.id}`,
          { method: 'DELETE', credentials: 'include' },
        );

        if (!response.ok) {
          throw new Error('Failed to delete payment');
        }

        refetchPayments();
        refetchAgreement();
        setPaymentModal((prev) => ({
          ...prev,
          deleteConfirmOpen: false,
          itemToDelete: null,
        }));
        showToast({
          message: 'Payment deleted successfully',
          color: 'success',
        });
      } catch (error) {
        console.error('Error deleting payment:', error);
        showToast({ message: 'Failed to delete payment', color: 'error' });
      } finally {
        setPaymentModal((prev) => ({
          ...prev,
          deleting: false,
          deleteConfirmOpen: false,
          itemToDelete: null,
        }));
      }
    },
    cancelDelete: () => {
      setPaymentModal((prev) => ({
        ...prev,
        deleteConfirmOpen: false,
        itemToDelete: null,
      }));
    },
    closeModal: () => {
      setPaymentModal((prev) => ({ ...prev, open: false }));
    },
    onSuccess: () => {
      refetchPayments();
      refetchAgreement();
      setPaymentModal((prev) => ({ ...prev, open: false }));
    },
  };

  // Agreement Delete Handlers
  const agreementHandlers = {
    delete: () => {
      setAgreementDeleteModal((prev) => ({
        ...prev,
        deleteConfirmOpen: true,
      }));
    },
    confirmDelete: async () => {
      setAgreementDeleteModal((prev) => ({ ...prev, deleting: true }));
      try {
        await deleteAgreementMutation.mutateAsync();
        showToast({
          message: 'Rebate agreement deleted successfully',
          color: 'success',
        });
        // Navigate back to agreements list after successful deletion
        navigate({ to: '/rebates/agreements' });
      } catch (error) {
        console.error('Error deleting rebate agreement:', error);
        showToast({
          message: 'Failed to delete rebate agreement',
          color: 'error',
        });
      } finally {
        setAgreementDeleteModal((prev) => ({
          ...prev,
          deleting: false,
          deleteConfirmOpen: false,
        }));
      }
    },
    cancelDelete: () => {
      setAgreementDeleteModal((prev) => ({
        ...prev,
        deleteConfirmOpen: false,
      }));
    },
  };

  // Navigation
  const handleBack = () => {
    navigate({ to: '/rebates/agreements' });
  };

  return {
    // State
    expandedSections,
    expandedRules,
    productGroupModal,
    rebateRuleModal,
    paymentModal,
    agreementDeleteModal,

    // Handlers
    handleAccordionChange,
    toggleRuleExpansion,
    productGroupHandlers,
    rebateRuleHandlers,
    paymentHandlers,
    agreementHandlers,
    handleBack,
  };
};
